import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  DragStartEvent,
  DragEndEvent,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  DynamicReportFilter,
  DynamicReportAggregation,
  PivotFieldInfo,
  PivotRequest,
  PivotResponse,
  ReportTemplate,
} from "../types";
import {
  dynamicReportService,
  reportTemplateService,
} from "../services/dataService";
import { formatCurrency, formatNumber, turkishIncludes } from "../utils/formatters";
import PageLoader from "../components/PageLoader";
import AlertModal from "../components/AlertModal";
import ConfirmModal from "../components/ConfirmModal";
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import "./DynamicReports.css";

type ChartType = "bar" | "line" | "pie";
type ViewMode = "table" | "chart" | "both";
type ZoneId = "rows" | "cols" | "values" | "filters";

const TEMPLATE_SOURCE = "UnifiedPivot";
const MAX_ROWS = 3;
const MAX_COLS = 2;

const CHART_COLORS = [
  "#4f46e5", "#0ea5e9", "#14b8a6", "#22c55e", "#f59e0b",
  "#ef4444", "#8b5cf6", "#ec4899", "#0891b2", "#64748b",
  "#2563eb", "#84cc16", "#f97316", "#06b6d4", "#a855f7",
];

const OPERATOR_LABELS: Record<string, string> = {
  eq: "Eşittir",
  neq: "Eşit Değil",
  gt: "Büyüktür",
  gte: "Büyük/Eşit",
  lt: "Küçüktür",
  lte: "Küçük/Eşit",
  contains: "İçerir",
  startswith: "İle Başlar",
  between: "Arasında",
  in: "Şunlardan biri",
};

const AGG_FUNCTIONS = [
  { value: "sum", label: "Toplam" },
  { value: "avg", label: "Ortalama" },
  { value: "min", label: "Min" },
  { value: "max", label: "Maks" },
  { value: "count", label: "Adet" },
];

const CURRENCIES = [
  { code: "TRY", label: "₺ TL" },
  { code: "USD", label: "$ USD" },
  { code: "EUR", label: "€ EUR" },
];

const COUNT_ONLY_MEASURES = ["OrderCount", "CostRowCount"];

// Kategori ikonları (sidebar grupları)
const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  "Tarih": <path d="M8 2v3M16 2v3M3.5 9h17M5 4.5h14a1.5 1.5 0 0 1 1.5 1.5v13A1.5 1.5 0 0 1 19 20.5H5A1.5 1.5 0 0 1 3.5 19V6A1.5 1.5 0 0 1 5 4.5z" />,
  "Sipariş": <path d="M6 3.5h12l2 4v12a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-12l2-4zM4 7.5h16M9.5 11a2.5 2.5 0 0 0 5 0" />,
  "Firma": <path d="M3 20.5h18M5 20.5V5a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v15.5M15 9.5h4a1 1 0 0 1 1 1v10M8 7.5h2M8 11h2M8 14.5h2" />,
  "Model": <path d="M20.4 14.5 16 10 4 20M14 4l2.5-1.5L19 4l-.5 3L21 9.5l-3 .5-1.5 2.5L15 10l-3-.5L14.5 7 14 4z" />,
  "Atölye": <path d="M2 20h20M4 20V8l6 4V8l6 4V8l4 2.5V20" />,
  "Teknik": <path d="M12 2 2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />,
  "Maliyet": <path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />,
  "Sayısal": <path d="M4 9h16M4 15h16M10 3 8 21M16 3l-2 18" />,
};

const operatorsFor = (type: string): string[] =>
  type === "datetime"
    ? ["between", "eq", "gt", "gte", "lt", "lte"]
    : type === "bool"
      ? ["eq", "neq"]
      : ["decimal", "int", "float", "currency"].includes(type)
        ? ["eq", "neq", "gt", "gte", "lt", "lte", "between"]
        : ["in", "eq", "neq", "contains", "startswith"];

// ── Çoklu seçim (in operatörü) ──
const MultiSelectChips: React.FC<{
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
}> = ({ options, selected, onChange }) => {
  const [search, setSearch] = useState("");
  const filtered = useMemo(
    () => (search ? options.filter((o) => turkishIncludes(o, search)) : options).slice(0, 300),
    [options, search],
  );
  const toggle = (v: string) =>
    onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);

  return (
    <div className="pbi-multiselect">
      <input
        className="pbi-multiselect-search"
        placeholder={`Ara… (${selected.length} seçili)`}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div className="pbi-multiselect-list">
        {filtered.length === 0 && <span className="pbi-hint">Değer yok</span>}
        {filtered.map((opt) => (
          <button
            key={opt}
            type="button"
            className={`pbi-ms-chip ${selected.includes(opt) ? "active" : ""}`}
            onClick={() => toggle(opt)}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
};

// ── Sütun başlığı değer filtresi (Excel pivot tarzı) ──
const HeaderFilterPopover: React.FC<{
  fieldLabel: string;
  options: string[];
  selected: string[];
  onApply: (vals: string[]) => void;
  onOpen: () => void;
}> = ({ fieldLabel, options, selected, onApply, onOpen }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState<string[]>(selected);

  const openPop = () => {
    setPicked(selected);
    setSearch("");
    setOpen(true);
    onOpen();
  };

  const filtered = useMemo(
    () => (search ? options.filter((o) => turkishIncludes(o, search)) : options).slice(0, 400),
    [options, search],
  );
  const toggle = (v: string) =>
    setPicked((p) => (p.includes(v) ? p.filter((x) => x !== v) : [...p, v]));
  const active = selected.length > 0;

  return (
    <div className="pv-hfilter">
      <button
        className={`pv-btn ghost sm ${active ? "pv-hfilter-active" : ""}`}
        onClick={() => (open ? setOpen(false) : openPop())}
        title={`${fieldLabel} sütunlarını filtrele`}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill={active ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
        </svg>
        {fieldLabel}
        {active && <em>{selected.length}</em>}
      </button>
      {open && (
        <>
          <div className="pv-hfilter-backdrop" onClick={() => setOpen(false)} />
          <div className="pv-hfilter-pop">
            <input
              className="pv-hfilter-search"
              placeholder="Değer ara…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
            <div className="pv-hfilter-list">
              {filtered.length === 0 && <span className="pbi-hint">Değer bulunamadı</span>}
              {filtered.map((opt) => (
                <label key={opt} className="pv-hfilter-item">
                  <input type="checkbox" checked={picked.includes(opt)} onChange={() => toggle(opt)} />
                  <span>{opt}</span>
                </label>
              ))}
            </div>
            <div className="pv-hfilter-foot">
              <button className="pv-link" onClick={() => setPicked([])}>Temizle</button>
              <span className="pv-hfilter-count">{picked.length > 0 ? `${picked.length} seçili` : "Tümü"}</span>
              <button className="pv-btn run sm" onClick={() => { setOpen(false); onApply(picked); }}>Uygula</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ── Sidebar'daki sürüklenebilir alan ──
const FieldSource: React.FC<{
  field: PivotFieldInfo;
  used: boolean;
  onQuick: (action: "rows" | "cols" | "filters" | "values") => void;
}> = ({ field, used, onQuick }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `src::${field.fieldName}`,
    data: { type: "source", field },
  });
  const isMeasure = field.role === "measure";
  const isFilterOnly = field.role === "filter";

  return (
    <div
      ref={setNodeRef}
      className={`pvf-item ${used ? "used" : ""} ${isDragging ? "dragging" : ""} ${isMeasure ? "measure" : ""}`}
      {...attributes}
      {...listeners}
    >
      <span className="pvf-grip" aria-hidden>
        <svg width="8" height="14" viewBox="0 0 8 14" fill="currentColor">
          <circle cx="2" cy="2" r="1.3" /><circle cx="6" cy="2" r="1.3" />
          <circle cx="2" cy="7" r="1.3" /><circle cx="6" cy="7" r="1.3" />
          <circle cx="2" cy="12" r="1.3" /><circle cx="6" cy="12" r="1.3" />
        </svg>
      </span>
      <span className="pvf-name" title={field.displayName}>{field.displayName}</span>
      {isMeasure && <span className="pvf-sigma">Σ</span>}
      <span className="pvf-quick" onPointerDown={(e) => e.stopPropagation()}>
        {isMeasure ? (
          <button className="pvf-qbtn val" title="Değerlere ekle" onClick={() => onQuick("values")}>+</button>
        ) : isFilterOnly ? (
          <button className="pvf-qbtn" title="Filtre ekle" onClick={() => onQuick("filters")}>F</button>
        ) : (
          <>
            <button className="pvf-qbtn" title="Satırlara ekle" onClick={() => onQuick("rows")}>S</button>
            <button className="pvf-qbtn" title="Sütunlara ekle" onClick={() => onQuick("cols")}>Sü</button>
            <button className="pvf-qbtn" title="Filtre ekle" onClick={() => onQuick("filters")}>F</button>
          </>
        )}
      </span>
    </div>
  );
};

// ── Kuyu içindeki sıralanabilir çip ──
const ZoneChip: React.FC<{
  id: string;
  zone: ZoneId;
  label: string;
  onRemove: () => void;
  fnSelect?: React.ReactNode;
}> = ({ id, zone, label, onRemove, fnSelect }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    data: { type: "chip", zone },
  });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={`pvz-chip ${zone} ${isDragging ? "dragging" : ""}`}
      {...attributes}
      {...listeners}
    >
      <span className="pvz-chip-grip" aria-hidden>⋮⋮</span>
      <span className="pvz-chip-label">{label}</span>
      {fnSelect}
      <button
        className="pvz-chip-x"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={onRemove}
        title="Kaldır"
      >
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
};

// ── Bırakma kuyusu ──
const Zone: React.FC<{
  zone: ZoneId;
  title: string;
  icon: React.ReactNode;
  hint: string;
  count?: string;
  children: React.ReactNode;
  isEmpty: boolean;
}> = ({ zone, title, icon, hint, count, children, isEmpty }) => {
  const { setNodeRef, isOver } = useDroppable({ id: `zone::${zone}`, data: { zone } });
  return (
    <div ref={setNodeRef} className={`pvz-well ${zone} ${isOver ? "is-over" : ""}`}>
      <div className="pvz-well-head">
        <span className="pvz-well-icon">{icon}</span>
        <span className="pvz-well-title">{title}</span>
        {count && <span className="pvz-well-count">{count}</span>}
      </div>
      <div className="pvz-well-body">
        {children}
        {isEmpty && (
          <div className="pvz-well-empty">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            {hint}
          </div>
        )}
      </div>
    </div>
  );
};

const DynamicReports: React.FC = () => {
  // Alan modeli
  const [fields, setFields] = useState<PivotFieldInfo[]>([]);
  const [loadingFields, setLoadingFields] = useState(true);
  const [fieldSearch, setFieldSearch] = useState("");
  const [openCats, setOpenCats] = useState<Record<string, boolean>>({});

  // Pivot yapılandırması
  const [rows, setRows] = useState<string[]>([]);
  const [cols, setCols] = useState<string[]>([]);
  const [measures, setMeasures] = useState<DynamicReportAggregation[]>([]);
  const [filters, setFilters] = useState<DynamicReportFilter[]>([]);
  const [targetCurrency, setTargetCurrency] = useState("TRY");
  const [rateMode, setRateMode] = useState("historical");

  // DnD
  const [activeDrag, setActiveDrag] = useState<{ label: string; kind: "dimension" | "measure" | "filter" | "chip"; zone?: ZoneId } | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // Değer listeleri
  const [fieldValues, setFieldValues] = useState<Record<string, string[]>>({});

  // Sonuç
  const [result, setResult] = useState<PivotResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Görünüm
  const [viewMode, setViewMode] = useState<ViewMode>("table");
  const [chartType, setChartType] = useState<ChartType>("bar");
  const [chartMeasureIdx, setChartMeasureIdx] = useState(0);
  const [showFilters, setShowFilters] = useState(true);
  // Sütun daraltma: 2 seviyede grup bazlı, 1 seviyede tümü
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [colsCollapsed, setColsCollapsed] = useState(false);

  // Şablonlar
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateDesc, setTemplateDesc] = useState("");
  const [templatePublic, setTemplatePublic] = useState(true);

  const [alertModal, setAlertModal] = useState<{ isOpen: boolean; message: string; type: "success" | "error" | "warning" | "info" }>(
    { isOpen: false, message: "", type: "info" });
  const [confirmModal, setConfirmModal] = useState<{ isOpen: boolean; message: string; type: "warning" | "danger"; onConfirm: () => void }>(
    { isOpen: false, message: "", type: "warning", onConfirm: () => {} });

  // ── Yükleme ──
  useEffect(() => {
    (async () => {
      try {
        setLoadingFields(true);
        const [f, t] = await Promise.all([
          dynamicReportService.getPivotFields(),
          reportTemplateService.getAll().catch(() => []),
        ]);
        setFields(f);
        setTemplates((t as ReportTemplate[]).filter((x) => x.dataSource === TEMPLATE_SOURCE));
      } catch (e) {
        console.error("Pivot alanları yüklenemedi", e);
        setAlertModal({ isOpen: true, message: "Rapor alanları yüklenemedi. API bağlantısını kontrol edin.", type: "error" });
      } finally {
        setLoadingFields(false);
      }
    })();
  }, []);

  const fieldMap = useMemo(() => {
    const m: Record<string, PivotFieldInfo> = {};
    fields.forEach((f) => (m[f.fieldName] = m[f.fieldName] || f));
    return m;
  }, [fields]);

  const label = useCallback(
    (name: string) => fieldMap[name]?.displayName || result?.fieldLabels?.[name] || name,
    [fieldMap, result],
  );

  const dimensionFields = useMemo(() => fields.filter((f) => f.role === "dimension"), [fields]);
  const measureFields = useMemo(() => fields.filter((f) => f.role === "measure"), [fields]);
  const filterableFields = useMemo(
    () => fields.filter((f) => f.role === "dimension" || f.role === "filter"),
    [fields],
  );

  const groupedSidebar = useMemo(() => {
    const source = fieldSearch
      ? fields.filter((f) => f.role !== "measure" && turkishIncludes(f.displayName, fieldSearch))
      : fields.filter((f) => f.role !== "measure");
    const groups: { category: string; items: PivotFieldInfo[] }[] = [];
    source.forEach((f) => {
      let g = groups.find((x) => x.category === f.category);
      if (!g) { g = { category: f.category, items: [] }; groups.push(g); }
      g.items.push(f);
    });
    return groups;
  }, [fields, fieldSearch]);

  const visibleMeasures = useMemo(
    () => (fieldSearch ? measureFields.filter((f) => turkishIncludes(f.displayName, fieldSearch)) : measureFields),
    [measureFields, fieldSearch],
  );

  const ensureFieldValues = useCallback(async (field: string) => {
    if (!field || fieldValues[field] !== undefined) return;
    setFieldValues((p) => ({ ...p, [field]: [] }));
    try {
      const vals = await dynamicReportService.getPivotFieldValues(field);
      setFieldValues((p) => ({ ...p, [field]: vals }));
    } catch { /* boş kalır */ }
  }, [fieldValues]);

  useEffect(() => {
    filters.forEach((f) => {
      if (fieldMap[f.field]?.hasValueList && fieldValues[f.field] === undefined)
        ensureFieldValues(f.field);
    });
  }, [filters, fieldMap, fieldValues, ensureFieldValues]);

  // ── Bölge işlemleri ──
  const markDirty = () => setDirty(true);

  const addToRows = (f: string) => {
    if (rows.includes(f)) return;
    if (rows.length >= MAX_ROWS)
      return setAlertModal({ isOpen: true, message: `En fazla ${MAX_ROWS} satır alanı seçilebilir.`, type: "warning" });
    setCols((p) => p.filter((x) => x !== f));
    setRows((p) => [...p, f]);
    markDirty();
  };
  const addToCols = (f: string) => {
    if (cols.includes(f)) return;
    if (cols.length >= MAX_COLS)
      return setAlertModal({ isOpen: true, message: `En fazla ${MAX_COLS} sütun alanı seçilebilir.`, type: "warning" });
    setRows((p) => p.filter((x) => x !== f));
    setCols((p) => [...p, f]);
    markDirty();
  };
  const addMeasure = (f: string) => {
    if (measures.some((m) => m.field === f)) return;
    setMeasures((p) => [...p, { field: f, function: "sum", alias: "" }]);
    markDirty();
  };
  const addFilter = (f?: string) => {
    const field = f || filterableFields[0]?.fieldName;
    if (!field) return;
    const ops = operatorsFor(fieldMap[field]?.dataType || "string");
    setFilters((p) => [...p, { field, operator: ops[0], value: "" }]);
    if (fieldMap[field]?.hasValueList) ensureFieldValues(field);
    setShowFilters(true);
    markDirty();
  };

  const removeRow = (f: string) => { setRows((p) => p.filter((x) => x !== f)); markDirty(); };
  const removeCol = (f: string) => { setCols((p) => p.filter((x) => x !== f)); markDirty(); };
  const removeMeasureAt = (i: number) => { setMeasures((p) => p.filter((_, idx) => idx !== i)); markDirty(); };
  const setMeasureFn = (i: number, fn: string) => {
    setMeasures((p) => p.map((m, idx) => (idx === i ? { ...m, function: fn } : m)));
    markDirty();
  };
  const updateFilter = (i: number, u: Partial<DynamicReportFilter>) => {
    setFilters((p) => p.map((x, idx) => (idx === i ? { ...x, ...u } : x)));
    if (u.field && fieldMap[u.field]?.hasValueList) ensureFieldValues(u.field);
    markDirty();
  };
  const removeFilter = (i: number) => { setFilters((p) => p.filter((_, idx) => idx !== i)); markDirty(); };

  // ── DnD işleyicileri ──
  const handleDragStart = (e: DragStartEvent) => {
    const d = e.active.data.current;
    if (d?.type === "source") {
      const f: PivotFieldInfo = d.field;
      setActiveDrag({ label: f.displayName, kind: f.role as any });
    } else if (d?.type === "chip") {
      const zone: ZoneId = d.zone;
      const id = String(e.active.id);
      const fieldName = id.split("::")[1];
      setActiveDrag({ label: label(fieldName), kind: "chip", zone });
    }
  };

  const resolveZone = (over: DragEndEvent["over"]): ZoneId | null => {
    if (!over) return null;
    const z = over.data.current?.zone as ZoneId | undefined;
    if (z) return z;
    const id = String(over.id);
    if (id.startsWith("zone::")) return id.slice(6) as ZoneId;
    return null;
  };

  const handleDragEnd = (e: DragEndEvent) => {
    setActiveDrag(null);
    const { active, over } = e;
    const d = active.data.current;
    const targetZone = resolveZone(over);

    if (d?.type === "source") {
      if (!targetZone) return;
      const f: PivotFieldInfo = d.field;
      if (f.role === "measure") {
        if (targetZone === "values") addMeasure(f.fieldName);
        return;
      }
      if (f.role === "filter") {
        if (targetZone === "filters") addFilter(f.fieldName);
        return;
      }
      // dimension
      if (targetZone === "rows") addToRows(f.fieldName);
      else if (targetZone === "cols") addToCols(f.fieldName);
      else if (targetZone === "filters") addFilter(f.fieldName);
      return;
    }

    if (d?.type === "chip") {
      const fromZone: ZoneId = d.zone;
      const activeId = String(active.id);
      const fieldName = activeId.split("::")[1];

      // Kuyu dışına bırakıldı → kaldır
      if (!targetZone) {
        if (fromZone === "rows") removeRow(fieldName);
        else if (fromZone === "cols") removeCol(fieldName);
        else if (fromZone === "values") {
          const i = measures.findIndex((m) => m.field === fieldName);
          if (i >= 0) removeMeasureAt(i);
        }
        return;
      }

      // Aynı kuyu içinde sıralama
      if (targetZone === fromZone && over) {
        const overId = String(over.id);
        if (overId.startsWith(`${fromZone}::`)) {
          const overField = overId.split("::")[1];
          if (fromZone === "rows") {
            const from = rows.indexOf(fieldName); const to = rows.indexOf(overField);
            if (from >= 0 && to >= 0 && from !== to) { setRows(arrayMove(rows, from, to)); markDirty(); }
          } else if (fromZone === "cols") {
            const from = cols.indexOf(fieldName); const to = cols.indexOf(overField);
            if (from >= 0 && to >= 0 && from !== to) { setCols(arrayMove(cols, from, to)); markDirty(); }
          } else if (fromZone === "values") {
            const from = measures.findIndex((m) => m.field === fieldName);
            const to = measures.findIndex((m) => m.field === overField);
            if (from >= 0 && to >= 0 && from !== to) { setMeasures(arrayMove(measures, from, to)); markDirty(); }
          }
        }
        return;
      }

      // Kuyular arası taşıma (boyut çipleri: satır ↔ sütun)
      if (fromZone === "rows" && targetZone === "cols") { removeRow(fieldName); addToCols(fieldName); }
      else if (fromZone === "cols" && targetZone === "rows") { removeCol(fieldName); addToRows(fieldName); }
      else if ((fromZone === "rows" || fromZone === "cols") && targetZone === "filters") { addFilter(fieldName); }
    }
  };

  // ── Çalıştırma ──
  const buildRequest = (): PivotRequest => ({
    rows,
    columns: cols,
    measures,
    filters: filters.filter((f) => (f.value ?? "").toString().trim() !== ""),
    targetCurrency,
    rateMode,
  });

  const executeReport = useCallback(async (req?: PivotRequest) => {
    const request = req || buildRequest();
    if (request.rows.length === 0)
      return setAlertModal({ isOpen: true, message: "En az bir satır alanı ekleyin.", type: "warning" });
    if (request.measures.length === 0)
      return setAlertModal({ isOpen: true, message: "En az bir değer (ölçü) ekleyin.", type: "warning" });

    setLoading(true);
    try {
      const res = await dynamicReportService.executePivot(request);
      setResult(res);
      setDirty(false);
      setChartMeasureIdx(0);
      setCollapsedGroups({});
      setColsCollapsed(false);
    } catch (e: any) {
      setAlertModal({
        isOpen: true,
        message: typeof e?.response?.data === "string" ? e.response.data : "Pivot rapor çalıştırılırken hata oluştu.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, cols, measures, filters, targetCurrency, rateMode]);

  const handleExportExcel = async () => {
    if (!result) return;
    setExporting(true);
    try {
      const blob = await dynamicReportService.exportPivotExcel(buildRequest());
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `pivot_rapor_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click();
      window.URL.revokeObjectURL(url); document.body.removeChild(a);
    } catch {
      setAlertModal({ isOpen: true, message: "Excel export sırasında hata oluştu.", type: "error" });
    } finally {
      setExporting(false);
    }
  };

  // ── Şablonlar ──
  const loadTemplates = async () => {
    try {
      const t = await reportTemplateService.getAll();
      setTemplates((t as ReportTemplate[]).filter((x) => x.dataSource === TEMPLATE_SOURCE));
    } catch { /* sessiz */ }
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim())
      return setAlertModal({ isOpen: true, message: "Şablon adı zorunludur.", type: "warning" });
    const config = { rows, cols, measures, filters, targetCurrency, rateMode };
    try {
      await reportTemplateService.create({
        name: templateName.trim(),
        description: templateDesc.trim() || undefined,
        dataSource: TEMPLATE_SOURCE,
        configJson: JSON.stringify(config),
        isPublic: templatePublic,
      });
      setAlertModal({ isOpen: true, message: "Şablon kaydedildi.", type: "success" });
      setShowSaveTemplate(false); setTemplateName(""); setTemplateDesc("");
      loadTemplates();
    } catch {
      setAlertModal({ isOpen: true, message: "Şablon kaydedilirken hata oluştu.", type: "error" });
    }
  };

  const handleLoadTemplate = (t: ReportTemplate, run: boolean) => {
    try {
      const c = JSON.parse(t.configJson);
      const nr = c.rows || []; const nc = c.cols || [];
      const nm = c.measures || []; const nf = c.filters || [];
      setRows(nr); setCols(nc); setMeasures(nm); setFilters(nf);
      setTargetCurrency(c.targetCurrency || "TRY");
      setRateMode(c.rateMode || "historical");
      setShowTemplates(false);
      if (run) {
        executeReport({
          rows: nr, columns: nc, measures: nm,
          filters: (nf as DynamicReportFilter[]).filter((f) => (f.value ?? "").toString().trim() !== ""),
          targetCurrency: c.targetCurrency || "TRY",
          rateMode: c.rateMode || "historical",
        });
      } else {
        setDirty(true);
      }
    } catch {
      setAlertModal({ isOpen: true, message: "Şablon yüklenirken hata oluştu.", type: "error" });
    }
  };

  const handleDeleteTemplate = (t: ReportTemplate) => {
    setConfirmModal({
      isOpen: true, type: "danger",
      message: `"${t.name}" şablonunu silmek istediğinize emin misiniz?`,
      onConfirm: async () => {
        try {
          await reportTemplateService.delete(t.reportTemplateId);
          loadTemplates();
        } catch {
          setAlertModal({ isOpen: true, message: "Şablon silinirken hata oluştu.", type: "error" });
        }
        setConfirmModal((p) => ({ ...p, isOpen: false }));
      },
    });
  };

  // ── Biçimleme ──
  const fmtMeasure = (v: number | null | undefined, dataType: string): string => {
    if (v == null) return "–";
    if (dataType === "currency") return formatCurrency(v, targetCurrency);
    if (dataType === "int") return formatNumber(v, 0);
    return formatNumber(v);
  };

  const chartData = useMemo(() => {
    if (!result || result.rowHeaders.length === 0) return [];
    const mi = Math.min(chartMeasureIdx, result.measures.length - 1);
    return result.rowHeaders
      .map((h, r) => ({ name: h.join(" / "), value: Number(result.rowTotals[r]?.[mi] ?? 0) }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 30);
  }, [result, chartMeasureIdx]);

  const hasCols = (result?.columnFields?.length ?? 0) > 0;
  const activeFilterCount = filters.filter((f) => (f.value ?? "").toString().trim() !== "").length;

  // Sütun daraltma yapısı: 2 sütun alanı → 1. seviye grupları
  const twoLevel = !!result && result.columnFields.length === 2 && (result.columnGroups?.length ?? 0) > 0;
  const renderGroups = useMemo(() => {
    if (!result || !twoLevel) return null;
    const heads = result.columnHeaders || [];
    return result.columnGroups.map((g, gi) => ({
      g,
      gi,
      collapsed: !!collapsedGroups[g],
      leaves: heads.map((h, i) => (h[0] === g ? i : -1)).filter((i) => i >= 0),
    }));
  }, [result, twoLevel, collapsedGroups]);

  const oneLevelCollapsed = hasCols && !twoLevel && colsCollapsed;
  const allCollapsed = twoLevel ? !!renderGroups?.every((rg) => rg.collapsed) : colsCollapsed;
  const toggleGroup = (g: string) =>
    setCollapsedGroups((p) => ({ ...p, [g]: !p[g] }));
  const toggleAllCols = () => {
    if (twoLevel && result) {
      const next = !allCollapsed;
      const map: Record<string, boolean> = {};
      result.columnGroups.forEach((g) => (map[g] = next));
      setCollapsedGroups(map);
    } else {
      setColsCollapsed((p) => !p);
    }
  };

  // ── Sütun değer filtresi (başlıktaki huni) ──
  const currentInFilter = (field: string): string[] => {
    const f = filters.find((x) => x.field === field && x.operator === "in");
    return f?.value ? f.value.split(",").filter(Boolean) : [];
  };

  const columnFilterOptions = (field: string): string[] => {
    const idx = result?.columnFields.indexOf(field) ?? -1;
    const fromResult = idx >= 0 && result
      ? Array.from(new Set((result.columnHeaders || []).map((h) => h[idx]).filter(Boolean)))
      : [];
    const fetched = fieldValues[field] || [];
    const sel = currentInFilter(field);
    return Array.from(new Set([...fetched, ...fromResult, ...sel]))
      .sort((a, b) => a.localeCompare(b, "tr"));
  };

  const applyColumnFilter = (field: string, vals: string[]) => {
    const others = filters.filter((x) => !(x.field === field && x.operator === "in"));
    const next = vals.length > 0
      ? [...others, { field, operator: "in", value: vals.join(",") }]
      : others;
    setFilters(next);
    executeReport({
      rows,
      columns: cols,
      measures,
      filters: next.filter((f) => (f.value ?? "").toString().trim() !== ""),
      targetCurrency,
      rateMode,
    });
  };

  if (loadingFields) return <PageLoader message="Rapor modeli yükleniyor..." />;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveDrag(null)}
    >
      <div className="pv-app">
        {/* ═══ SIDEBAR — ALAN KİTAPLIĞI ═══ */}
        <aside className="pv-sidebar">
          <div className="pv-sidebar-head">
            <div className="pv-sidebar-brand">
              <span className="pv-brand-mark">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                  <rect x="3" y="3" width="18" height="18" rx="3" /><line x1="3" y1="9.5" x2="21" y2="9.5" /><line x1="9.5" y1="3" x2="9.5" y2="21" />
                </svg>
              </span>
              <div>
                <div className="pv-sidebar-title">Alan Kitaplığı</div>
                <div className="pv-sidebar-sub">Sürükleyip kuyulara bırakın</div>
              </div>
            </div>
          </div>

          <div className="pv-search-wrap">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input placeholder="Alan ara…" value={fieldSearch} onChange={(e) => setFieldSearch(e.target.value)} />
            {fieldSearch && <button className="pv-search-clear" onClick={() => setFieldSearch("")}>×</button>}
          </div>

          <div className="pv-sidebar-body">
            {/* Boyut grupları */}
            {groupedSidebar.map((g) => {
              const open = openCats[g.category] !== false; // varsayılan açık
              return (
                <div key={g.category} className="pv-cat">
                  <button
                    className="pv-cat-head"
                    onClick={() => setOpenCats((p) => ({ ...p, [g.category]: !open }))}
                  >
                    <span className="pv-cat-icon">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                        {CATEGORY_ICONS[g.category] || <circle cx="12" cy="12" r="8" />}
                      </svg>
                    </span>
                    <span className="pv-cat-name">{g.category}</span>
                    <span className="pv-cat-count">{g.items.length}</span>
                    <span className={`pv-cat-caret ${open ? "open" : ""}`}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="6 9 12 15 18 9" /></svg>
                    </span>
                  </button>
                  {open && (
                    <div className="pv-cat-body">
                      {g.items.map((f) => (
                        <FieldSource
                          key={f.fieldName}
                          field={f}
                          used={rows.includes(f.fieldName) || cols.includes(f.fieldName)}
                          onQuick={(a) => {
                            if (a === "rows") addToRows(f.fieldName);
                            else if (a === "cols") addToCols(f.fieldName);
                            else if (a === "filters") addFilter(f.fieldName);
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Ölçüler */}
            <div className="pv-cat measures">
              <div className="pv-cat-head static">
                <span className="pv-cat-icon sigma">Σ</span>
                <span className="pv-cat-name">Ölçüler</span>
                <span className="pv-cat-count">{visibleMeasures.length}</span>
              </div>
              <div className="pv-cat-body">
                {visibleMeasures.map((f) => (
                  <FieldSource
                    key={f.fieldName}
                    field={f}
                    used={measures.some((m) => m.field === f.fieldName)}
                    onQuick={() => {
                      const i = measures.findIndex((m) => m.field === f.fieldName);
                      if (i >= 0) removeMeasureAt(i); else addMeasure(f.fieldName);
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </aside>

        {/* ═══ ANA ALAN ═══ */}
        <main className="pv-main">
          {/* Üst bar */}
          <header className="pv-topbar">
            <div className="pv-topbar-l">
              <h1>Pivot Raporlar</h1>
              {result && (
                <div className="pv-meta">
                  <span className="pv-meta-pill">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M6 3.5h12l2 4v12a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-12l2-4z" /></svg>
                    {result.orderCount.toLocaleString("tr-TR")} sipariş
                  </span>
                  <span className="pv-meta-pill dim">{result.recordCount.toLocaleString("tr-TR")} kayıt</span>
                </div>
              )}
            </div>
            <div className="pv-topbar-r">
              {/* Para birimi */}
              <div className="pv-seg">
                {CURRENCIES.map((c) => (
                  <button key={c.code} className={targetCurrency === c.code ? "active" : ""}
                    onClick={() => { setTargetCurrency(c.code); markDirty(); }}>{c.label}</button>
                ))}
              </div>
              <div className="pv-seg">
                <button className={rateMode === "historical" ? "active" : ""} title="Kaydın kendi tarihindeki kur"
                  onClick={() => { setRateMode("historical"); markDirty(); }}>O günkü kur</button>
                <button className={rateMode === "current" ? "active" : ""} title="En güncel TCMB kuru"
                  onClick={() => { setRateMode("current"); markDirty(); }}>Güncel</button>
              </div>

              <span className="pv-topbar-sep" />

              <div className="pv-seg icons">
                <button className={viewMode === "table" ? "active" : ""} onClick={() => setViewMode("table")} title="Tablo">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="16" rx="2" /><line x1="3" y1="10" x2="21" y2="10" /><line x1="10" y1="10" x2="10" y2="20" /></svg>
                </button>
                <button className={viewMode === "chart" ? "active" : ""} onClick={() => setViewMode("chart")} title="Grafik">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg>
                </button>
                <button className={viewMode === "both" ? "active" : ""} onClick={() => setViewMode("both")} title="Her ikisi">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="18" height="7" rx="1" /></svg>
                </button>
              </div>

              <button className="pv-btn ghost" onClick={() => setShowTemplates(true)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" /></svg>
                Şablonlar
                {templates.length > 0 && <em>{templates.length}</em>}
              </button>
              <button className="pv-btn ghost" onClick={() => setShowSaveTemplate(true)} title="Şablon olarak kaydet">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                Kaydet
              </button>
              <button className="pv-btn excel" onClick={handleExportExcel} disabled={exporting || !result || result.rowHeaders.length === 0}>
                {exporting ? <span className="pv-spin" /> : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                )}
                Excel
              </button>
              <button className={`pv-btn run ${dirty ? "attention" : ""}`} onClick={() => executeReport()} disabled={loading}>
                {loading ? <span className="pv-spin light" /> : (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="6 3 20 12 6 21 6 3" /></svg>
                )}
                Çalıştır
              </button>
            </div>
          </header>

          {/* ═══ KUYULAR (drag-drop bölgeleri) ═══ */}
          <section className="pv-shelf">
            <Zone
              zone="rows"
              title="Satırlar"
              count={`${rows.length}/${MAX_ROWS}`}
              hint="Boyut sürükleyin"
              isEmpty={rows.length === 0}
              icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="4" y1="6" x2="20" y2="6" /><line x1="4" y1="12" x2="20" y2="12" /><line x1="4" y1="18" x2="20" y2="18" /></svg>}
            >
              <SortableContext items={rows.map((f) => `rows::${f}`)} strategy={horizontalListSortingStrategy}>
                {rows.map((f) => (
                  <ZoneChip key={f} id={`rows::${f}`} zone="rows" label={label(f)} onRemove={() => removeRow(f)} />
                ))}
              </SortableContext>
            </Zone>

            <Zone
              zone="cols"
              title="Sütunlar"
              count={`${cols.length}/${MAX_COLS}`}
              hint="Opsiyonel"
              isEmpty={cols.length === 0}
              icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="6" y1="4" x2="6" y2="20" /><line x1="12" y1="4" x2="12" y2="20" /><line x1="18" y1="4" x2="18" y2="20" /></svg>}
            >
              <SortableContext items={cols.map((f) => `cols::${f}`)} strategy={horizontalListSortingStrategy}>
                {cols.map((f) => (
                  <ZoneChip key={f} id={`cols::${f}`} zone="cols" label={label(f)} onRemove={() => removeCol(f)} />
                ))}
              </SortableContext>
            </Zone>

            <Zone
              zone="values"
              title="Değerler"
              count={measures.length > 0 ? String(measures.length) : undefined}
              hint="Ölçü sürükleyin (Σ)"
              isEmpty={measures.length === 0}
              icon={<span className="pvz-sigma">Σ</span>}
            >
              <SortableContext items={measures.map((m) => `values::${m.field}`)} strategy={horizontalListSortingStrategy}>
                {measures.map((m, i) => (
                  <ZoneChip
                    key={m.field}
                    id={`values::${m.field}`}
                    zone="values"
                    label={label(m.field)}
                    onRemove={() => removeMeasureAt(i)}
                    fnSelect={!COUNT_ONLY_MEASURES.includes(m.field) ? (
                      <select
                        className="pvz-chip-fn"
                        value={m.function}
                        onPointerDown={(e) => e.stopPropagation()}
                        onChange={(e) => setMeasureFn(i, e.target.value)}
                      >
                        {AGG_FUNCTIONS.map((fn) => <option key={fn.value} value={fn.value}>{fn.label}</option>)}
                      </select>
                    ) : undefined}
                  />
                ))}
              </SortableContext>
            </Zone>

            <Zone
              zone="filters"
              title="Filtreler"
              count={filters.length > 0 ? `${activeFilterCount}/${filters.length}` : undefined}
              hint="Alan sürükleyin"
              isEmpty={filters.length === 0}
              icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>}
            >
              {filters.map((f, i) => (
                <button
                  key={i}
                  className={`pvz-chip filter as-btn ${(f.value ?? "").trim() !== "" ? "" : "pending"}`}
                  onClick={() => setShowFilters(true)}
                  title="Filtre panelini aç"
                >
                  <span className="pvz-chip-label">
                    {label(f.field)}
                    {(f.value ?? "").trim() !== "" && <em> · {OPERATOR_LABELS[f.operator]}</em>}
                  </span>
                  <span
                    className="pvz-chip-x"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => { e.stopPropagation(); removeFilter(i); }}
                  >
                    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                  </span>
                </button>
              ))}
            </Zone>
          </section>

          {/* Filtre düzenleme paneli */}
          {filters.length > 0 && showFilters && (
            <section className="pv-filterpanel">
              <div className="pv-filterpanel-head">
                <span>Filtre Detayları</span>
                <button className="pv-link" onClick={() => setShowFilters(false)}>Gizle</button>
              </div>
              <div className="pv-filterpanel-body">
                {filters.map((f, idx) => {
                  const info = fieldMap[f.field];
                  const type = info?.dataType || "string";
                  const listVals = fieldValues[f.field] || [];
                  return (
                    <div key={idx} className="pv-filter-row">
                      <select value={f.field} onChange={(e) => {
                        const ops = operatorsFor(fieldMap[e.target.value]?.dataType || "string");
                        updateFilter(idx, { field: e.target.value, operator: ops[0], value: "", value2: undefined });
                      }}>
                        {filterableFields.map((c) => <option key={c.fieldName} value={c.fieldName}>{c.displayName}</option>)}
                      </select>
                      <select value={f.operator} onChange={(e) => updateFilter(idx, { operator: e.target.value })}>
                        {operatorsFor(type).map((op) => <option key={op} value={op}>{OPERATOR_LABELS[op]}</option>)}
                      </select>
                      {type === "datetime" ? (
                        <input type="date" value={f.value} onChange={(e) => updateFilter(idx, { value: e.target.value })} />
                      ) : type === "bool" ? (
                        <select value={f.value} onChange={(e) => updateFilter(idx, { value: e.target.value })}>
                          <option value="">Seçin</option><option value="true">Evet</option><option value="false">Hayır</option>
                        </select>
                      ) : info?.hasValueList && f.operator === "in" ? (
                        <MultiSelectChips
                          options={listVals}
                          selected={f.value ? f.value.split(",").filter(Boolean) : []}
                          onChange={(next) => updateFilter(idx, { value: next.join(",") })}
                        />
                      ) : info?.hasValueList ? (
                        <>
                          <input list={`pvl-${idx}`} value={f.value} placeholder="Seç veya yaz" onChange={(e) => updateFilter(idx, { value: e.target.value })} />
                          <datalist id={`pvl-${idx}`}>
                            {listVals.slice(0, 500).map((v) => <option key={v} value={v} />)}
                          </datalist>
                        </>
                      ) : (
                        <input
                          type={["decimal", "int", "float", "currency"].includes(type) ? "number" : "text"}
                          value={f.value}
                          placeholder="Değer"
                          onChange={(e) => updateFilter(idx, { value: e.target.value })}
                        />
                      )}
                      {f.operator === "between" && (
                        <input
                          type={type === "datetime" ? "date" : "number"}
                          value={f.value2 || ""}
                          placeholder="Üst değer"
                          onChange={(e) => updateFilter(idx, { value2: e.target.value })}
                        />
                      )}
                      <button className="pv-icon-btn danger" onClick={() => removeFilter(idx)} title="Filtreyi sil">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                      </button>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* ═══ SONUÇ ═══ */}
          <div className="pv-body">
            {loading && !result && <PageLoader message="Rapor çalıştırılıyor..." />}

            {!result && !loading && (
              <div className="pv-empty">
                <div className="pv-empty-visual">
                  <div className="pv-empty-grid">
                    <span /><span /><span /><span className="hl" /><span /><span className="hl" /><span /><span /><span />
                  </div>
                </div>
                <h2>Raporunuzu tasarlayın</h2>
                <p>
                  Soldaki <strong>Alan Kitaplığı</strong>'ndan alanları yukarıdaki
                  <span className="pv-empty-tag rows">Satırlar</span>,
                  <span className="pv-empty-tag cols">Sütunlar</span> ve
                  <span className="pv-empty-tag vals">Değerler</span> kuyularına sürükleyin,
                  ardından <strong>Çalıştır</strong>'a basın.
                </p>
              </div>
            )}

            {result && (
              <>
                {/* KPI şeridi */}
                {result.measures.length > 0 && (
                  <div className="pv-kpis">
                    {result.measures.slice(0, 6).map((m, i) => (
                      <div key={m.alias} className="pv-kpi">
                        <span className="pv-kpi-dot" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                        <div className="pv-kpi-text">
                          <span className="pv-kpi-value">{fmtMeasure(result.grandTotals[i] as number, m.dataType)}</span>
                          <span className="pv-kpi-label">{m.label}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Grafik */}
                {viewMode !== "table" && chartData.length > 0 && (
                  <div className="pv-card">
                    <div className="pv-card-head">
                      <span className="pv-card-title">Grafik</span>
                      <span className="pv-card-sub">{result.measures[Math.min(chartMeasureIdx, result.measures.length - 1)]?.label} · ilk {chartData.length}</span>
                      <div className="pv-card-tools">
                        {result.measures.length > 1 && (
                          <select value={chartMeasureIdx} onChange={(e) => setChartMeasureIdx(Number(e.target.value))}>
                            {result.measures.map((m, i) => <option key={m.alias} value={i}>{m.label}</option>)}
                          </select>
                        )}
                        <div className="pv-seg icons">
                          <button className={chartType === "bar" ? "active" : ""} onClick={() => setChartType("bar")} title="Sütun">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg>
                          </button>
                          <button className={chartType === "line" ? "active" : ""} onClick={() => setChartType("line")} title="Çizgi">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 17 9 11 13 15 21 6" /></svg>
                          </button>
                          <button className={chartType === "pie" ? "active" : ""} onClick={() => setChartType("pie")} title="Pasta">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.21 15.89A10 10 0 1 1 8 2.83" /><path d="M22 12A10 10 0 0 0 12 2v10z" /></svg>
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="pv-card-body">
                      <ResponsiveContainer width="100%" height={Math.max(340, chartData.length * 14)}>
                        {chartType === "pie" ? (
                          <PieChart>
                            <Pie data={chartData.slice(0, 12)} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={115} innerRadius={60} paddingAngle={2}
                              label={({ name, percent }: any) => (percent > 0.05 ? `${name}: %${((percent || 0) * 100).toFixed(0)}` : "")}>
                              {chartData.slice(0, 12).map((_, idx) => <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />)}
                            </Pie>
                            <Tooltip formatter={(v: any) => formatNumber(Number(v))} />
                          </PieChart>
                        ) : chartType === "line" ? (
                          <LineChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 60 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" vertical={false} />
                            <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748b" }} angle={-45} textAnchor="end" height={90} />
                            <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => formatNumber(v)} width={70} />
                            <Tooltip formatter={(v: any) => formatNumber(Number(v))} />
                            <Line type="monotone" dataKey="value" stroke="#4f46e5" strokeWidth={2.5} dot={{ r: 3 }} />
                          </LineChart>
                        ) : (
                          <BarChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 60 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" vertical={false} />
                            <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748b" }} angle={-45} textAnchor="end" height={90} />
                            <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => formatNumber(v)} width={70} />
                            <Tooltip formatter={(v: any) => formatNumber(Number(v))} />
                            <Bar dataKey="value" radius={[5, 5, 0, 0]}>
                              {chartData.map((_, idx) => <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />)}
                            </Bar>
                          </BarChart>
                        )}
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {/* Pivot tablo */}
                {viewMode !== "chart" && (
                  <div className="pv-card grow">
                    <div className="pv-card-head">
                      <span className="pv-card-title">Pivot Tablo</span>
                      <span className="pv-card-sub">
                        {result.rowHeaders.length.toLocaleString("tr-TR")} satır
                        {hasCols ? ` × ${result.columnKeys.length} sütun` : ""} · {result.measures.length} ölçü
                      </span>
                      {hasCols && (
                        <div className="pv-card-tools">
                          {result.columnFields.map((f) => (
                            <HeaderFilterPopover
                              key={f}
                              fieldLabel={label(f)}
                              options={columnFilterOptions(f)}
                              selected={currentInFilter(f)}
                              onApply={(vals) => applyColumnFilter(f, vals)}
                              onOpen={() => ensureFieldValues(f)}
                            />
                          ))}
                          <button className="pv-btn ghost sm" onClick={toggleAllCols} title={allCollapsed ? "Tüm sütunları genişlet" : "Tüm sütunları daralt — toplamları göster"}>
                            {allCollapsed ? (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" /><line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" /></svg>
                            ) : (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="4 14 10 14 10 20" /><polyline points="20 10 14 10 14 4" /><line x1="14" y1="10" x2="21" y2="3" /><line x1="3" y1="21" x2="10" y2="14" /></svg>
                            )}
                            {allCollapsed ? "Sütunları Genişlet" : "Sütunları Daralt"}
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="pv-card-body table">
                      {result.rowHeaders.length === 0 ? (
                        <div className="pv-noresult">Filtrelerle eşleşen veri bulunamadı</div>
                      ) : (
                        <div className="pv-table-scroll">
                          <table className="pv-table">
                            <thead>
                              <tr>
                                {result.rowFields.map((f, i) => (
                                  <th key={f} rowSpan={hasCols ? 2 : 1} className={`pv-th-row sticky-${i}`}>{label(f)}</th>
                                ))}
                                {!hasCols ? (
                                  result.measures.map((m) => <th key={m.alias} className="pv-th-measure">{m.label}</th>)
                                ) : oneLevelCollapsed ? (
                                  <th colSpan={result.measures.length} className="pv-th-colgroup clickable collapsed" onClick={() => setColsCollapsed(false)} title="Sütunları genişlet">
                                    <span className="pv-caret">▸</span> {result.columnKeys.length} sütun gizli · Genel Toplam
                                  </th>
                                ) : twoLevel && renderGroups ? (
                                  <>
                                    {renderGroups.map((rg) => (
                                      <th
                                        key={rg.g}
                                        colSpan={(rg.collapsed ? 1 : rg.leaves.length) * result.measures.length}
                                        className={`pv-th-colgroup clickable ${rg.collapsed ? "collapsed" : ""}`}
                                        onClick={() => toggleGroup(rg.g)}
                                        title={rg.collapsed ? "Genişlet" : "Daralt — toplamı göster"}
                                      >
                                        <span className="pv-caret">{rg.collapsed ? "▸" : "▾"}</span> {rg.g}
                                        {rg.collapsed && <span className="pv-hidden-note"> · {rg.leaves.length} sütun</span>}
                                      </th>
                                    ))}
                                    <th colSpan={result.measures.length} className="pv-th-total">Genel Toplam</th>
                                  </>
                                ) : (
                                  <>
                                    {result.columnKeys.map((ck) => (
                                      <th key={ck} colSpan={result.measures.length} className="pv-th-colgroup">{ck}</th>
                                    ))}
                                    <th colSpan={result.measures.length} className="pv-th-total">Genel Toplam</th>
                                  </>
                                )}
                              </tr>
                              {hasCols && (
                                <tr>
                                  {oneLevelCollapsed ? (
                                    result.measures.map((m) => (
                                      <th key={"c" + m.alias} className="pv-th-measure total">{m.label}</th>
                                    ))
                                  ) : twoLevel && renderGroups ? (
                                    <>
                                      {renderGroups.map((rg) =>
                                        rg.collapsed
                                          ? result.measures.map((m) => (
                                            <th key={rg.g + m.alias} className="pv-th-measure sub">
                                              {result.measures.length > 1 ? m.label : "Toplam"}
                                            </th>
                                          ))
                                          : rg.leaves.flatMap((li) => result.measures.map((m) => (
                                            <th key={li + m.alias} className="pv-th-measure">
                                              {result.measures.length > 1
                                                ? `${result.columnHeaders[li][1]} · ${m.label}`
                                                : result.columnHeaders[li][1]}
                                            </th>
                                          ))),
                                      )}
                                      {result.measures.map((m) => (
                                        <th key={"gt" + m.alias} className="pv-th-measure total">{m.label}</th>
                                      ))}
                                    </>
                                  ) : (
                                    <>
                                      {result.columnKeys.map((ck) => result.measures.map((m) => (
                                        <th key={ck + m.alias} className="pv-th-measure">{m.label}</th>
                                      )))}
                                      {result.measures.map((m) => (
                                        <th key={"gt" + m.alias} className="pv-th-measure total">{m.label}</th>
                                      ))}
                                    </>
                                  )}
                                </tr>
                              )}
                            </thead>
                            <tbody>
                              {result.rowHeaders.map((hdr, r) => (
                                <tr key={r}>
                                  {hdr.map((v, i) => <td key={i} className={`pv-td-row sticky-${i}`}>{v}</td>)}
                                  {!hasCols || oneLevelCollapsed ? (
                                    result.measures.map((m, mi) => (
                                      <td key={m.alias} className={`pv-td-num ${oneLevelCollapsed ? "total" : ""}`}>{fmtMeasure(result.rowTotals[r][mi] as number, m.dataType)}</td>
                                    ))
                                  ) : twoLevel && renderGroups ? (
                                    <>
                                      {renderGroups.map((rg) =>
                                        rg.collapsed
                                          ? result.measures.map((m, mi) => (
                                            <td key={rg.g + m.alias} className="pv-td-num sub">{fmtMeasure(result.groupCells[r][rg.gi][mi] as number, m.dataType)}</td>
                                          ))
                                          : rg.leaves.flatMap((li) => result.measures.map((m, mi) => (
                                            <td key={li + m.alias} className="pv-td-num">{fmtMeasure(result.cells[r][li][mi] as number, m.dataType)}</td>
                                          ))),
                                      )}
                                      {result.measures.map((m, mi) => (
                                        <td key={"rt" + m.alias} className="pv-td-num total">{fmtMeasure(result.rowTotals[r][mi] as number, m.dataType)}</td>
                                      ))}
                                    </>
                                  ) : (
                                    <>
                                      {result.columnKeys.map((ck, c) => result.measures.map((m, mi) => (
                                        <td key={ck + m.alias} className="pv-td-num">{fmtMeasure(result.cells[r][c][mi] as number, m.dataType)}</td>
                                      )))}
                                      {result.measures.map((m, mi) => (
                                        <td key={"rt" + m.alias} className="pv-td-num total">{fmtMeasure(result.rowTotals[r][mi] as number, m.dataType)}</td>
                                      ))}
                                    </>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr>
                                <td colSpan={result.rowFields.length} className="pv-tf-label sticky-0">Genel Toplam</td>
                                {!hasCols || oneLevelCollapsed ? (
                                  result.measures.map((m, mi) => (
                                    <td key={"g" + m.alias} className="pv-td-num total">{fmtMeasure(result.grandTotals[mi] as number, m.dataType)}</td>
                                  ))
                                ) : twoLevel && renderGroups ? (
                                  <>
                                    {renderGroups.map((rg) =>
                                      rg.collapsed
                                        ? result.measures.map((m, mi) => (
                                          <td key={rg.g + m.alias} className="pv-td-num">{fmtMeasure(result.groupTotals[rg.gi][mi] as number, m.dataType)}</td>
                                        ))
                                        : rg.leaves.flatMap((li) => result.measures.map((m, mi) => (
                                          <td key={li + m.alias} className="pv-td-num">{fmtMeasure(result.columnTotals[li][mi] as number, m.dataType)}</td>
                                        ))),
                                    )}
                                    {result.measures.map((m, mi) => (
                                      <td key={"g" + m.alias} className="pv-td-num total">{fmtMeasure(result.grandTotals[mi] as number, m.dataType)}</td>
                                    ))}
                                  </>
                                ) : (
                                  <>
                                    {result.columnKeys.map((ck, c) => result.measures.map((m, mi) => (
                                      <td key={"ct" + ck + m.alias} className="pv-td-num">{fmtMeasure(result.columnTotals[c][mi] as number, m.dataType)}</td>
                                    )))}
                                    {result.measures.map((m, mi) => (
                                      <td key={"g" + m.alias} className="pv-td-num total">{fmtMeasure(result.grandTotals[mi] as number, m.dataType)}</td>
                                    ))}
                                  </>
                                )}
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>

      {/* Sürükleme hayaleti */}
      <DragOverlay dropAnimation={null}>
        {activeDrag && (
          <div className={`pv-drag-ghost ${activeDrag.kind === "measure" || activeDrag.zone === "values" ? "measure" : ""}`}>
            {(activeDrag.kind === "measure" || activeDrag.zone === "values") && <span className="sigma">Σ</span>}
            {activeDrag.label}
          </div>
        )}
      </DragOverlay>

      {/* ═══ OVERLAYS ═══ */}
      {showTemplates && (
        <div className="pv-overlay" onClick={() => setShowTemplates(false)}>
          <div className="pv-panel" onClick={(e) => e.stopPropagation()}>
            <div className="pv-panel-head">
              <h2>Kayıtlı Şablonlar</h2>
              <button className="pv-icon-btn" onClick={() => setShowTemplates(false)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            <div className="pv-panel-body">
              {templates.length === 0
                ? <div className="pv-noresult">Henüz kayıtlı pivot şablonu yok</div>
                : <div className="pv-template-list">
                    {templates.map((t) => (
                      <div key={t.reportTemplateId} className="pv-template">
                        <div className="pv-template-info">
                          <h4>{t.name}</h4>
                          {t.description && <p>{t.description}</p>}
                          <div className="pv-template-meta">
                            {t.isPublic && <span className="pv-tag green">Herkese Açık</span>}
                            {t.createdByUserName && <span className="pv-tag">{t.createdByUserName}</span>}
                          </div>
                        </div>
                        <div className="pv-template-actions">
                          <button className="pv-btn run sm" onClick={() => handleLoadTemplate(t, true)} title="Çalıştır">
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="6 3 20 12 6 21 6 3" /></svg>
                          </button>
                          <button className="pv-btn ghost sm" onClick={() => handleLoadTemplate(t, false)} title="Yükle">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /><path d="M4 19V5" /></svg>
                          </button>
                          <button className="pv-icon-btn danger" onClick={() => handleDeleteTemplate(t)} title="Sil">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>}
            </div>
          </div>
        </div>
      )}

      {showSaveTemplate && (
        <div className="pv-overlay" onClick={() => setShowSaveTemplate(false)}>
          <div className="pv-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Şablon Olarak Kaydet</h3>
            <div className="pv-form">
              <div className="pv-form-group">
                <label>Şablon Adı <span className="req">*</span></label>
                <input type="text" value={templateName} onChange={(e) => setTemplateName(e.target.value)} placeholder="Örn: Firma × Maliyet Kategorisi" autoFocus />
              </div>
              <div className="pv-form-group">
                <label>Açıklama</label>
                <textarea value={templateDesc} onChange={(e) => setTemplateDesc(e.target.value)} placeholder="Rapor açıklaması" rows={2} />
              </div>
              <label className="pv-check">
                <input type="checkbox" checked={templatePublic} onChange={(e) => setTemplatePublic(e.target.checked)} /> Herkese açık şablon
              </label>
            </div>
            <div className="pv-modal-actions">
              <button className="pv-btn ghost" onClick={() => setShowSaveTemplate(false)}>İptal</button>
              <button className="pv-btn run" onClick={handleSaveTemplate}>Kaydet</button>
            </div>
          </div>
        </div>
      )}

      <AlertModal isOpen={alertModal.isOpen} message={alertModal.message} type={alertModal.type}
        onClose={() => setAlertModal((p) => ({ ...p, isOpen: false }))} />
      <ConfirmModal isOpen={confirmModal.isOpen} message={confirmModal.message} type={confirmModal.type}
        onConfirm={confirmModal.onConfirm} onCancel={() => setConfirmModal((p) => ({ ...p, isOpen: false }))} />
    </DndContext>
  );
};

export default DynamicReports;
